/**
 * Admin Setup → Platform: allowed email domains for customer users.
 *
 * When the list is non-empty, inviting a user to THIS company only
 * accepts addresses on the listed domains — the guard against personal
 * or mistyped addresses landing on the customer side. Suppliers are
 * never restricted. Empty list = no restriction (the default).
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { MailCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../../lib/api';

export function AllowedEmailDomainsCard() {
  const [domains, setDomains] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    onboardingAPI.getOnboardingConfig()
      .then((res) => setDomains(res.data.allowed_user_email_domains || []))
      .catch((err: unknown) => console.error('Failed to load domain policy:', err))
      .finally(() => setLoading(false));
  }, []);

  const save = async (next: string[]) => {
    setSaving(true);
    try {
      const res = await onboardingAPI.updateOnboardingConfig({
        allowed_user_email_domains: next,
      });
      setDomains(res.data.allowed_user_email_domains || []);
      toast.success(
        next.length
          ? 'Allowed domains saved — customer-user invites are now restricted to them.'
          : 'Restriction removed — any email domain can be invited.',
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { allowed_user_email_domains?: string[] } } };
      toast.error(
        axiosErr.response?.data?.allowed_user_email_domains?.[0]
        || 'Failed to save allowed domains',
      );
    } finally {
      setSaving(false);
    }
  };

  const addDraft = () => {
    const domain = draft.trim().replace(/^@/, '').toLowerCase();
    if (!domain) return;
    if (domains.includes(domain)) {
      setDraft('');
      return;
    }
    void save([...domains, domain]);
    setDraft('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MailCheck className="h-5 w-5" />
          Allowed User Email Domains
        </CardTitle>
        <CardDescription>
          When set, new users invited to this company must have an email on
          one of these domains. Leave empty for no restriction. Supplier
          users are never restricted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="example.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
            disabled={loading || saving}
            className="max-w-xs"
          />
          <Button onClick={addDraft} disabled={loading || saving || !draft.trim()}>
            Add domain
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {loading ? (
            <span className="text-sm text-muted-foreground">Loading…</span>
          ) : domains.length === 0 ? (
            <span className="text-sm text-muted-foreground">
              No restriction — any email domain can be invited.
            </span>
          ) : (
            domains.map((d) => (
              <Badge key={d} variant="secondary" className="gap-1 pr-1">
                {d}
                <button
                  type="button"
                  onClick={() => void save(domains.filter((x) => x !== d))}
                  disabled={saving}
                  className="rounded-full hover:bg-muted-foreground/20 p-0.5"
                  aria-label={`Remove ${d}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
